import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * An OnlyFans list paged by a marker instead of an offset (posts page by
 * `beforePublishTime=<tailMarker>`). A new `fetchPage` starts over; a slow
 * page from a previous filter never lands on top of the new one.
 */
export interface CursorPage<T> {
  list: T[]
  hasMore: boolean
  next: string | null
}

export function useCursorList<T>(fetchPage: (cursor: string | null) => Promise<CursorPage<T>>, idOf: (row: T) => string | number) {
  const [items, setItems] = useState<T[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const state = useRef({ fetchPage, cursor: null as string | null, hasMore: true, busy: false, seen: new Set<string | number>() })

  const loadMore = useCallback(async () => {
    const s = state.current
    if (s.busy || !s.hasMore) return
    const mine = s.fetchPage
    s.busy = true
    setLoading(true)
    setFailed(false)
    try {
      const page = await mine(s.cursor)
      if (state.current.fetchPage !== mine) return
      const fresh = page.list.filter((row) => {
        const id = idOf(row)
        if (s.seen.has(id)) return false
        s.seen.add(id)
        return true
      })
      s.cursor = page.next
      s.hasMore = page.hasMore && fresh.length > 0 && page.next !== null
      setItems((all) => [...all, ...fresh])
      setHasMore(s.hasMore)
    } catch {
      if (state.current.fetchPage === mine) setFailed(true)
    } finally {
      if (state.current.fetchPage === mine) {
        s.busy = false
        setLoading(false)
      }
    }
  }, [idOf])

  useEffect(() => {
    state.current = { fetchPage, cursor: null, hasMore: true, busy: false, seen: new Set() }
    setItems([])
    setHasMore(true)
    void loadMore()
  }, [fetchPage, loadMore])

  return { items, setItems, hasMore, loading, failed, loadMore }
}
